import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useMemo, useReducer } from 'react';
import { useNavigate } from 'react-router';

import type { OAuthError } from '@auth0/auth0-react';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from 'api/init';
import { APPLICATION_TYPES, REDIRECT_TO_LOGIN_URL } from 'appConstants';
import { AppLoader } from 'shared';

import AppContext from './AppContext';
import { getActions } from './actions';
import { initialState, reducer } from './reducer';
import * as selectors from './selectors';

const AppProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);
  const actions = useMemo(() => getActions(dispatch), [dispatch]);

  const { getCurrentUser, setApplicationType } = actions;
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const { userSettingsRequested, userSettingsLoading } = state;
  useEffect(() => {
    (async () => {
      if (userSettingsLoading || userSettingsRequested || isLoading) return;
      try {
        const accessToken = await getAccessTokenSilently({
          detailedResponse: true,
        });
        if (accessToken?.access_token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken?.access_token}`;
          getCurrentUser();
        }
      } catch (error) {
        const expectedErrors = [
          'missing_refresh_token',
          'invalid_grant',
          'login_required',
          'consent_required',
        ];
        const err = error as OAuthError;

        if (err && !expectedErrors.includes(err.error)) {
          console.error('Unhandled Auth0 error:', error);
          return;
        }

        if (err.error === 'login_required' || err.error === 'consent_required') {
          loginWithRedirect();
          return;
        }
        if (
          !window.location.search.includes('code=') &&
          !window.location.search.includes('error=')
        ) {
          navigate(REDIRECT_TO_LOGIN_URL);
        }
      }
    })();
  }, [
    getCurrentUser,
    getAccessTokenSilently,
    isAuthenticated,
    navigate,
    userSettingsLoading,
    userSettingsRequested,
    isLoading,
    loginWithRedirect,
  ]);

  useEffect(() => {
    if (state.isGroupUser) {
      setApplicationType(APPLICATION_TYPES.FERTILISER);
    }
  }, [state.isGroupUser, setApplicationType]);

  return (
    <AppContext.Provider value={{ actions, selectors, state }}>
      {!userSettingsRequested || userSettingsLoading || !isAuthenticated ? <AppLoader /> : children}
    </AppContext.Provider>
  );
};

export default AppProvider;
